import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Bewijsstuk } from '@app/core/interfaces/bewijsstukken';
import { UploadService } from '@app/core/upload/upload.service';
import { DocumentsService } from '@app/my-zone/pages/claims/store/effects/documents.service';
import { ConfirmationModalComponent } from '@app/my-zone/shared/modals/confirmation-modal/confirmation.modal';
import { UploadFormService } from '@app/my-zone/shared/paperwork/documentary-evidence/upload-form/upload-form.service';
import { UploaderOptions, UploadInput, UploadOutput } from '@angular-ex/uploader';
import { NewModal } from 'outshared-lib';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'ins-upload-form',
    templateUrl: './upload-form.component.html',
    styleUrls: ['./upload-form.component.scss'],
})
export class UploadFormComponent implements OnDestroy, OnInit {
    @Output() readonly uploadDocument = new EventEmitter();
    @Output() readonly deleteDocument = new EventEmitter();

    @Input() evidenceDocuments: Bewijsstuk;
    @Input() errors: { messages: { boodschap_inhoud: string }[] };
    @Input() public evidence: Bewijsstuk[] = [];
    @Input() evidenceIndex: number;

    public lastUpload$: Observable<any> = this.uploadFormService.lastUpload$;
    public isLoading$: Observable<any> = this.uploadFormService.isLoading$;
    public uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();
    public loading: boolean = false;
    public uploadOptions: UploaderOptions = this.upload.options;

    private subscriptions = new Subscription();
    private lastUpload: string = null;

    constructor(
        public upload: UploadService,
        private uploadFormService: UploadFormService,
        private changeDetector: ChangeDetectorRef,
        private documentsService: DocumentsService,
        private modalService: NewModal
    ) {
        const lastUploadSubscription = this.lastUpload$.subscribe((lastUpload) => {
            if (lastUpload === this.lastUpload) {
                this.clearUpload();
            }
        });
        this.subscriptions.add(lastUploadSubscription);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.handleDocument();
    }

    public onUploadOutput(output: UploadOutput, isExtraDocument?: boolean): void {
        switch (output.type) {
            case 'start':
                this.loading = true;
                break;
            case 'allAddedToQueue':
                const event: UploadInput = {
                    type: 'uploadAll',
                    url: this.upload.options.url,
                    method: 'POST',
                    data: {},
                };

                this.uploadInput.emit(event);
                break;
            case 'done':
                this.upload.handle(output.file);
                this.uploadFile(this.upload.getFirst(), isExtraDocument);
                break;
            case 'rejected':
                this.errors = {
                    messages: [
                        {
                            boodschap_inhoud: $localize`:@@uploadFormComponent.fileSizeError:Het bestand dat u probeert te uploaden is te groot. De maximale bestandsgrootte is ${this.upload?.maximumFileSizeInMB} MB.`,
                        },
                    ],
                };
                break;
        }
    }

    public isDisabled(): boolean {
        return this.upload.files.length > 0;
    }

    public onDeleteClick(documentItem): void {
        const modalRef = this.modalService.open(ConfirmationModalComponent);

        modalRef.componentInstance.model = {
            title: 'Document verwijderen?',
            body: `Weet u zeker dat u het document ${documentItem.pg_document_bestandsnaam} wilt verwijderen?`,
            bevestiging: 'Bevestigen',
            ontkenning: 'Annuleren',
        };

        const modalSubscription = modalRef.afterClosed$().subscribe((response) => {
            if (response) {
                this.deleteDocument.emit({
                    dossier_volgnummer: this.evidenceDocuments.dossier_volgnummer,
                    upload_enveloppe_volgnummer: documentItem.upload_enveloppe_volgnummer,
                    upload_enveloppe_item_volgnummer: documentItem.upload_enveloppe_item_volgnummer,
                    bewijsstuk_volgnummer: this.evidenceDocuments.bewijsstuk_volgnummer,
                });

                this.handleDocument();
            }
        });

        this.subscriptions.add(modalSubscription);
    }

    private uploadFile(upload: object, isExtraDocument: boolean): void {
        const _upload = {
            document_id: isExtraDocument ? 'EXDO' : this.evidence[this.evidenceIndex].document_id,
            data_base64: upload['encoded'],
            bestandsnaam: upload['name'],
        };

        this.lastUpload = upload['name'];
        this.uploadDocument.emit({
            dossier_volgnummer: this.evidenceDocuments.dossier_volgnummer,
            bewijsstuk_volgnummer: this.evidenceDocuments.bewijsstuk_volgnummer,
            upload: _upload,
        });
    }

    private handleDocument(): void {
        this.documentsService
            .getDocuments$(this.evidenceDocuments.dossier_volgnummer)
            .pipe(
                tap((res) => {
                    this.evidence = res.data.bewijsstuk;

                    if (this.evidenceDocuments.document_id === 'EXDO') {
                        this.evidence = [...this.evidence, this.evidenceDocuments];
                    }
                    this.changeDetector.detectChanges();
                })
            )
            .subscribe();
    }

    private clearUpload(): void {
        this.upload.clear();
        this.loading = false;
    }
}
